import { useContext } from "react";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaAdvokatkredsFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { advokatkredse } from "../../../../../util/advokatkreds";
import { f } from "../../../../../util/f";
import { AccordionSubFilterItem } from "../../../../accordion/Accordion";
import DefaultDropdownItem from "../../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "advokatkreds";

export default function FirmaAdvokatkredsFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // // find active settings
  const settings: FirmaAdvokatkredsFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const advokatkredsSorted = advokatkredse.sort((a, b) => a.nr - b.nr);

  // handlers
  function updateFilterHandler(selectedItem: { kode: number; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.id === selectedItem.kode)) {
      settings.push({ id: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        settings.map((set) => {
          return {
            retskredsNrNavigation: {
              advokatkredsNr: {
                eq: set.id,
              },
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaAdvokatkredsFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: FirmaAdvokatkredsFilterSetting["id"]) {
    const updatedSettings: FirmaAdvokatkredsFilterSetting[] =
      settings.filter((set) => {
        return id !== set.id;
      }) || [];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            retskredsNrNavigation: {
              advokatkredsNr: {
                eq: set.id,
              },
            },
          };
        }),
      )
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Advokatkreds"
      value="advokat"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={advokatkredsSorted}
        itemsIdKey="name"
        itemToString={(item) => item?.name ?? ""}
        itemToElement={(item) => <DefaultDropdownItem label={`${item.name}`} />}
        placeholder="Vælg advokatkreds ..."
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.nr && item.selectedItem.name) {
            updateFilterHandler({
              kode: item.selectedItem.nr,
              name: item.selectedItem.name,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
